import { type AssetType, isFreestyleAssetType } from 'src/data/asset';
import { getLanguageLabel } from 'src/data/generation';
import {
  convertRewriteToolToSuggestTool,
  formatToolDisplayName,
  type GenerationTool,
  getGenerationToolForAssetType,
  getMultiPromptSubtools,
  isFreestyleGenerationTool,
  isImageGenerationTool,
  isLongFormLocalizationTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import type { MultiPromptTemplate } from 'src/data/template';
import { isSectionRecomposeVariation, type Variation } from 'src/data/variation';

export const formatShortFormVariationDescription = ({
  variation,
  multiPromptTemplate,
}: {
  variation: Variation;
  multiPromptTemplate?: MultiPromptTemplate;
}) => {
  // variations from new "improve sections" results
  if (isSectionRecomposeVariation(variation)) {
    return formatToolDisplayName(variation.generationTool, { assetType: variation.assetType });
  }

  if (variation.customMultiPromptId || variation.multiPromptTool) {
    const stepIndex = Number(variation.multiPromptStepIndex);
    let stepName = variation.multiPromptStepName;
    if (!stepName && stepIndex > 0) {
      if (multiPromptTemplate) {
        stepName = multiPromptTemplate?.blocks?.[stepIndex]?.name;
      } else if (variation.multiPromptTool) {
        const tools = getMultiPromptSubtools(variation.multiPromptTool);
        if (tools) {
          stepName = Object.keys(tools)[stepIndex - 1];
        }
      }
    }

    const baseTool = isImageGenerationTool(variation.generationTool)
      ? variation.multiPromptTool
      : variation.generationTool;

    const resolvedTool =
      baseTool && !isFreestyleGenerationTool(baseTool) && !isLongFormLocalizationTool(baseTool)
        ? baseTool
        : variation.classifiedGenerationTool || variation.generationTool;

    // Chained localization tags every step with the generic `lf_localization`
    // tool; resolve it to the asset's own tool so it reads "Facebook Post -
    // Text (Russian)" instead of the generic "Text (Russian)".
    const alignedTool = alignLocalizationToolToAsset(resolvedTool, variation.assetType);

    return formatToolDisplayName(alignedTool, {
      assetType: variation.assetType,
      hasImage: isImageGenerationTool(variation.generationTool),
      isImageOnly: isImageGenerationTool(variation.generationTool) && !variation.copyText,
      stepName,
    });
  }

  const alignedTool = alignSinglePromptTool(variation);

  // in case this is a "localization" variation - display the language name in the title
  const localizedLanguageLabel =
    isLongFormLocalizationTool(variation.generationTool) && variation.language
      ? getLanguageLabel(variation.language)
      : undefined;

  return formatToolDisplayName(alignedTool, {
    assetType: variation.assetType,
    isImageOnly: isImageGenerationTool(variation.generationTool) && !variation.copyText,
    stepName: localizedLanguageLabel,
  });
};

const alignSinglePromptTool = (variation: Variation): GenerationTool => {
  const localizationTool = alignLocalizationToolToAsset(
    variation.generationTool,
    variation.assetType
  );
  if (localizationTool !== variation.generationTool) {
    return localizationTool;
  }

  return isRewriteGenerationTool(variation.generationTool)
    ? convertRewriteToolToSuggestTool(variation.generationTool)
    : variation.generationTool;
};

// A localization variation carries the generic `lf_localization` tool. For a
// concrete (non-freestyle) asset, resolve it to that asset's own tool so the
// label reads "Asset Tool - Asset (Language)" rather than the generic
// localization label. Returns the tool unchanged when it isn't localization.
const alignLocalizationToolToAsset = (
  tool: GenerationTool,
  assetType: AssetType
): GenerationTool => {
  if (isLongFormLocalizationTool(tool) && !isFreestyleAssetType(assetType)) {
    return getGenerationToolForAssetType(assetType) ?? tool;
  }

  return tool;
};
